/**
 * Pre-lock edit form for a draft job. Reuses the same per-job slice as the
 * Create page's accordion row: device type / service tier / service mode /
 * urgency / summary / requirements / details / internal notes / preliminary
 * charges composer.
 *
 * Renders only when the session lifecycle is pre-lock; the page picks the
 * render mode upstream.
 */

import React, { useState } from 'react';
import { Alert, Stack } from '@mui/material';
import type {
  CheckInChargeLine,
  CheckInDeviceType,
  CheckInDraftJob,
  CheckInDraftJobUpdate,
  CheckInIntakeType,
} from '@techgitter/contracts';

import { patchCheckInDraftJob } from '../../../services/checkInApi';
import {
  DraftJobAccordionRow,
  type DraftJobFormState,
} from '../DraftJobAccordionRow';
import type {
  CheckInExpediteTier,
  CheckInServiceMode,
  CheckInServiceTier,
} from '../../../data/checkInServices';

export interface DeviceEditFormProps {
  sessionId: string;
  draftJob: CheckInDraftJob;
  /** Session-level context forwarded to the charges editor for the
   *  return-grace / waive logic. Both default to null when the session
   *  isn't a return. */
  intakeType?: CheckInIntakeType | null;
  originalWorkItemId?: string | null;
  /** Drives the default-no-diagnostic-fee behaviour in the charges
   *  editor. True when the session belongs to a Core or Core+
   *  designated customer (billed via monthly statement). */
  isCoreCustomer?: boolean;
  onSaved: () => void;
}

export const DeviceEditForm: React.FC<DeviceEditFormProps> = ({
  sessionId,
  draftJob,
  intakeType = null,
  originalWorkItemId = null,
  isCoreCustomer = false,
  onSaved,
}) => {
  const isExpressSession = intakeType === 'express';
  const [job, setJob] = useState<DraftJobFormState>(() => ({
    clientId: draftJob.id,
    deviceType: draftJob.deviceType as CheckInDeviceType,
    deviceTypeOther: draftJob.deviceTypeOther ?? '',
    serviceMode: ((draftJob as unknown as { serviceMode?: CheckInServiceMode }).serviceMode ?? 'diagnose_and_quote') as CheckInServiceMode,
    serviceTier: 'repair' as CheckInServiceTier['id'],
    urgency: 'standard' as CheckInExpediteTier['id'],
    serviceSummary: draftJob.serviceSummary ?? '',
    requirements: draftJob.requirements ?? '',
    customerVisibleDetails: draftJob.customerVisibleDetails ?? '',
    internalNotes: draftJob.internalNotes ?? '',
    // Express sessions never carry prelim charges — money is collected
    // via the monthly statement, not at check-in. Force an empty list
    // here so saving an Express session also wipes any stale legacy
    // charges (e.g. a $90 diagnostic that an earlier build of the
    // device-edit form auto-composed before we plumbed isExpress
    // through to the charges editor).
    preliminaryCharges: isExpressSession ? [] : (draftJob.preliminaryCharges ?? []),
  }));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const update = (patch: Partial<DraftJobFormState>) => setJob(prev => ({ ...prev, ...patch }));
  const setCharges = (charges: CheckInChargeLine[]) =>
    setJob(prev => ({ ...prev, preliminaryCharges: charges }));

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const patch: CheckInDraftJobUpdate = {
        serviceMode: job.serviceMode,
        serviceSummary: job.serviceSummary,
        requirements: job.requirements || null,
        customerVisibleDetails: job.customerVisibleDetails || null,
        internalNotes: job.internalNotes || null,
        preliminaryCharges: job.preliminaryCharges,
        // Correctable, but only on a device stored as `other` — the server
        // refuses it on any other type, and the type itself is not editable.
        ...(draftJob.deviceType === 'other' ? { deviceTypeOther: job.deviceTypeOther.trim() } : {}),
      };
      await patchCheckInDraftJob(sessionId, draftJob.id, patch);
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Stack spacing={2}>
      <DraftJobAccordionRow
        index={0}
        job={job}
        expanded
        showRemove={false}
        isExpress={isExpressSession}
        intakeType={intakeType}
        originalWorkItemId={originalWorkItemId}
        isCoreCustomer={isCoreCustomer}
        deviceTypeLocked
        onToggle={() => undefined}
        onChange={update}
        onChargesChange={setCharges}
        onRemove={() => undefined}
      />

      {error && <Alert severity="error">{error}</Alert>}

      {/* Inline, not in the sticky bar: on an in-store device that bar is the
          inspection's "Mark inspection complete", and these fields save on
          their own terms (charges are not something to autosave). */}
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button
          type="button"
          className="ci-btn ci-btn--primary ci-btn--touch"
          onClick={handleSave}
          disabled={
            saving ||
            !job.serviceSummary.trim() ||
            (draftJob.deviceType === 'other' && !job.deviceTypeOther.trim())
          }
        >
          {saving ? 'Saving…' : 'Save changes'}
        </button>
      </div>
    </Stack>
  );
};
